import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Image as ImageIcon, UploadSimple, CheckCircle, MusicNote, Play, Stop, Check, Phone } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppSettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    support_phone: '',
    whatsapp_link: '',
    support_chat_link: '',
    customer_logo_url: '',
    web_link: '',
    app_name: '',
    privacy_policy_text: '',
    terms_conditions_text: '',
    support_24x7_link: '',
    customer_login_link: '',
    telegram_link: '',
    instagram_link: '',
    deposit_option_1: '',
    deposit_option_2: '',
    deposit_option_3: '',
    deposit_option_4: ''
  });

  const [previews, setPreviews] = useState({
    appLogo: null,
    webpageLogo: null,
    customerLogo: null
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setFormData({
        support_phone: response.data.support_phone || '',
        whatsapp_link: response.data.whatsapp_link || '',
        support_chat_link: response.data.support_chat_link || '',
        customer_logo_url: response.data.customer_logo_url || '',
        web_link: response.data.web_link || '',
        app_name: response.data.app_name || 'DWS Vault Chat',
        privacy_policy_text: response.data.privacy_policy_text || '',
        terms_conditions_text: response.data.terms_conditions_text || '',
        support_24x7_link: response.data.support_24x7_link || '',
        customer_login_link: response.data.customer_login_link || '',
        telegram_link: response.data.telegram_link || '',
        instagram_link: response.data.instagram_link || '',
        deposit_option_1: response.data.deposit_option_1 || 'I want deposit less than 5K',
        deposit_option_2: response.data.deposit_option_2 || 'I want deposit more than 5K',
        deposit_option_3: response.data.deposit_option_3 || 'I want deposit 5K to 10K',
        deposit_option_4: response.data.deposit_option_4 || 'I want deposit more than 10K'
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogoUpload = async (e, logoType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (event) => {
      if (logoType === 'customer') {
        setPreviews(prev => ({ ...prev, customerLogo: event.target.result }));
      } else {
        setPreviews(prev => ({
          ...prev,
          [logoType === 'app' ? 'appLogo' : 'webpageLogo']: event.target.result
        }));
      }
    };
    reader.readAsDataURL(file);

    try {
      setUploading(true);
      
      if (logoType === 'customer') {
        // Upload customer logo via different endpoint
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        const response = await axios.post(`${API}/upload/customer-logo`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Construct full URL
        const fullLogoUrl = `${BACKEND_URL}${response.data.file_url}`;
        
        // Update settings with new logo URL
        await axios.put(`${API}/settings/update`, {
          customer_logo_url: fullLogoUrl
        });
        
        toast.success('Customer logo uploaded!');
        
        // Wait a bit for backend to save
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('logo_type', logoType);

        await axios.post(`${API}/upload/logo`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        toast.success(`${logoType === 'app' ? 'App' : 'Webpage'} logo uploaded!`);
      }
      
      // Reload settings to get updated URLs
      await loadSettings();
      setUploading(false);
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
      setUploading(false);
    }
  };



  const handleUpdateSupport = async () => {
    try {
      setLoading(true);
      await axios.put(`${API}/settings/update`, formData);
      toast.success('Support links updated!');
      setLoading(false);
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
      setLoading(false);
    }
  };

  
 

  return (
    <div className="space-y-6">
    

    

      {/* Support Links */}
      <div className="bg-wa-panel rounded-lg border border-wa-border p-6">
        <h2 className="text-lg font-bold text-wa-text-primary mb-4">Support Links & App Settings</h2>

        <div className="space-y-4">
          {/* App Name */}
          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              App Name
            </label>
            <input
              type="text"
              name="app_name"
              value={formData.app_name}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., DWS Vault Chat"
            />
            <p className="text-xs text-wa-text-secondary mt-1">App name shown across the platform</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              Support Phone Number
            </label>
            <input
              type="text"
              name="support_phone"
              value={formData.support_phone}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., +919876543210"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              WhatsApp Link
            </label>
            <input
              type="text"
              name="whatsapp_link"
              value={formData.whatsapp_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://wa.me/919876543210"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              Telegram Link
            </label>
            <input
              type="text"
              name="telegram_link"
              value={formData.telegram_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://t.me/your_channel"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              Instagram Link
            </label>
            <input
              type="text"
              name="instagram_link"
              value={formData.instagram_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://instagram.com/your_page"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              Support Chat Link
            </label>
            <input
              type="text"
              name="support_chat_link"
              value={formData.support_chat_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://support.example.com/chat"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">
              Customer Login Link
            </label>
            <input
              type="text"
              name="customer_login_link"
              value={formData.customer_login_link}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., https://your-app.com/customer-login"
            />
            <p className="text-xs text-wa-text-secondary mt-1">This link is sent to customers when registered from chat</p>
          </div>

          {/* Privacy Policy Rich Text Editor */}
          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">Privacy Policy</label>
            <div className="border border-wa-border rounded-lg overflow-hidden">
              <div className="bg-wa-hover border-b border-wa-border px-3 py-2 flex flex-wrap gap-1">
                <button type="button" onClick={() => { const el = document.getElementById('privacy-editor'); document.execCommand('bold'); el.focus(); }} className="px-2 py-1 text-xs font-bold text-wa-text-primary hover:bg-wa-hover rounded">B</button>
                <button type="button" onClick={() => { const el = document.getElementById('privacy-editor'); document.execCommand('italic'); el.focus(); }} className="px-2 py-1 text-xs italic text-wa-text-primary hover:bg-wa-hover rounded">I</button>
                <button type="button" onClick={() => { const el = document.getElementById('privacy-editor'); document.execCommand('underline'); el.focus(); }} className="px-2 py-1 text-xs underline text-wa-text-primary hover:bg-wa-hover rounded">U</button>
                <span className="border-l border-wa-border mx-1" />
                <select onChange={(e) => { document.execCommand('fontName', false, e.target.value); document.getElementById('privacy-editor').focus(); }} className="text-xs border border-wa-border bg-wa-panel text-wa-text-primary rounded px-1 py-0.5">
                  <option value="Arial">Arial</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Trebuchet MS">Trebuchet MS</option>
                </select>
                <select onChange={(e) => { document.execCommand('fontSize', false, e.target.value); document.getElementById('privacy-editor').focus(); }} className="text-xs border border-wa-border bg-wa-panel text-wa-text-primary rounded px-1 py-0.5">
                  <option value="2">Small</option>
                  <option value="3" selected>Normal</option>
                  <option value="4">Large</option>
                  <option value="5">XL</option>
                </select>
                <span className="border-l border-wa-border mx-1" />
                <button type="button" onClick={() => { document.execCommand('insertUnorderedList'); document.getElementById('privacy-editor').focus(); }} className="px-2 py-1 text-xs text-wa-text-primary hover:bg-wa-hover rounded">List</button>
              </div>
              <div
                id="privacy-editor"
                contentEditable
                className="min-h-[120px] px-4 py-3 text-sm bg-wa-panel text-wa-text-primary focus:outline-none"
                dangerouslySetInnerHTML={{ __html: formData.privacy_policy_text }}
                onBlur={(e) => setFormData(prev => ({ ...prev, privacy_policy_text: e.target.innerHTML }))}
              />
            </div>
            <p className="text-xs text-wa-text-secondary mt-1">Shown on Customer Login page</p>
          </div>

          {/* Terms & Conditions Rich Text Editor */}
          <div>
            <label className="block text-sm font-medium text-wa-text-primary mb-2">Terms & Conditions</label>
            <div className="border border-wa-border rounded-lg overflow-hidden">
              <div className="bg-wa-hover border-b border-wa-border px-3 py-2 flex flex-wrap gap-1">
                <button type="button" onClick={() => { const el = document.getElementById('terms-editor'); document.execCommand('bold'); el.focus(); }} className="px-2 py-1 text-xs font-bold text-wa-text-primary hover:bg-wa-hover rounded">B</button>
                <button type="button" onClick={() => { const el = document.getElementById('terms-editor'); document.execCommand('italic'); el.focus(); }} className="px-2 py-1 text-xs italic text-wa-text-primary hover:bg-wa-hover rounded">I</button>
                <button type="button" onClick={() => { const el = document.getElementById('terms-editor'); document.execCommand('underline'); el.focus(); }} className="px-2 py-1 text-xs underline text-wa-text-primary hover:bg-wa-hover rounded">U</button>
                <span className="border-l border-wa-border mx-1" />
                <select onChange={(e) => { document.execCommand('fontName', false, e.target.value); document.getElementById('terms-editor').focus(); }} className="text-xs border border-wa-border bg-wa-panel text-wa-text-primary rounded px-1 py-0.5">
                  <option value="Arial">Arial</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Trebuchet MS">Trebuchet MS</option>
                </select>
                <select onChange={(e) => { document.execCommand('fontSize', false, e.target.value); document.getElementById('terms-editor').focus(); }} className="text-xs border border-wa-border bg-wa-panel text-wa-text-primary rounded px-1 py-0.5">
                  <option value="2">Small</option>
                  <option value="3" selected>Normal</option>
                  <option value="4">Large</option>
                  <option value="5">XL</option>
                </select>
                <span className="border-l border-wa-border mx-1" />
                <button type="button" onClick={() => { document.execCommand('insertUnorderedList'); document.getElementById('terms-editor').focus(); }} className="px-2 py-1 text-xs text-wa-text-primary hover:bg-wa-hover rounded">List</button>
              </div>
              <div
                id="terms-editor"
                contentEditable
                className="min-h-[120px] px-4 py-3 text-sm bg-wa-panel text-wa-text-primary focus:outline-none"
                dangerouslySetInnerHTML={{ __html: formData.terms_conditions_text }}
                onBlur={(e) => setFormData(prev => ({ ...prev, terms_conditions_text: e.target.innerHTML }))}
              />
            </div>
            <p className="text-xs text-wa-text-secondary mt-1">Shown on Customer Login page</p>
          </div>

          {/* Deposit Range Option Buttons Configuration */}
          <div className="border-t border-wa-border pt-6 mt-6">
            <h3 className="text-base font-bold text-wa-text-primary mb-2">💰 Customer Deposit Option Buttons</h3>
            <p className="text-xs text-wa-text-secondary mb-4">
              Configure the buttons shown to customers in the deposit options popup modal (at least 2 options are required).
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-wa-text-secondary mb-1">
                  Deposit Option 1 (Required)
                </label>
                <input
                  type="text"
                  name="deposit_option_1"
                  value={formData.deposit_option_1}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-wa-border bg-wa-panel text-wa-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  placeholder="e.g., I want deposit less than 5K"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-wa-text-secondary mb-1">
                  Deposit Option 2 (Required)
                </label>
                <input
                  type="text"
                  name="deposit_option_2"
                  value={formData.deposit_option_2}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-wa-border bg-wa-panel text-wa-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  placeholder="e.g., I want deposit more than 5K"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-wa-text-secondary mb-1">
                  Deposit Option 3 (Optional)
                </label>
                <input
                  type="text"
                  name="deposit_option_3"
                  value={formData.deposit_option_3}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-wa-border bg-wa-panel text-wa-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  placeholder="e.g., I want deposit between 5K - 10K"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-wa-text-secondary mb-1">
                  Deposit Option 4 (Optional)
                </label>
                <input
                  type="text"
                  name="deposit_option_4"
                  value={formData.deposit_option_4}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-wa-border bg-wa-panel text-wa-text-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  placeholder="e.g., Custom deposit range"
                />
              </div>
            </div>
          </div>

          <button
            onClick={handleUpdateSupport}
            disabled={loading}
            className="w-full px-4 py-3 bg-wa-primary-strong text-white rounded-lg hover:bg-wa-primary-strong disabled:opacity-50 font-semibold flex items-center justify-center gap-2 mt-4"
          >
            <CheckCircle size={20} weight="bold" />
            {loading ? 'Updating...' : 'Update Settings & Buttons'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppSettingsManagement;
